import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { RideStatusBadge } from "common-fe";
// #1805 — every timestamp fmt() below (changeset.at, marksConfirmedAt,
// completedAt, statusChangedAt, adminConfirmedAt) is a genuine server-stamped
// audit instant (`new Date().toISOString()`), never an FVD Access stamp, so
// it belongs on the show's real venueTimezone (#1811 split), not the fixed
// FVD stamp zone. No show.venueTimezone is wired into this dialog yet (no
// urgency, Jaybill 2026-08-21 on #1805), so this reuses the shared
// resolver's Pacific fallback rather than a duplicate local literal.
import { DEFAULT_VENUE_TIMEZONE } from "common-fe/venueTime";
import { useTranslation } from "i18n-fe";
import { formatPoints } from "score-common";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { formatPlacing } from "../util/placingDisplay";
import SheetStateChip, { deriveSheetState } from "./SheetStateChip";

// #587 — the read+UI half of the two-stage audit (#571). Not secret: any
// admin looking at a Ride can open this from a judge's tab. Presents facts
// only — as-confirmed / as-signed / current, who/when/why per changeset — no
// suspicion scoring, no flagging (#586 was killed deliberately; don't
// reintroduce it here).

function fmt(at) {
  if (!at) return "—";
  try {
    // #612 / #1805 — render audit-trail instants venue-local, through the
    // shared resolver's DEFAULT_VENUE_TIMEZONE (was a local
    // "America/Los_Angeles" literal) — the viewer's browser timezone must
    // never leak into an audit trail. Distinct from util/classDisplay.js's
    // FVD_STAMP_TIMEZONE: these are genuine server instants, not FVD stamps.
    return new Date(at).toLocaleString([], {
      timeZone: DEFAULT_VENUE_TIMEZONE,
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return at;
  }
}

// Union of movement numbers across whichever of the three snapshots exist,
// so a movement only present in one still gets a row instead of silently
// dropping.
function movementReconciliation(confirmedSnap, signedSnap, currentSnap) {
  const numbers = new Set();
  for (const snap of [confirmedSnap, signedSnap, currentSnap]) {
    for (const m of snap?.movementScores ?? []) numbers.add(m.movementNumber);
  }
  return [...numbers]
    .sort((a, b) => a - b)
    .map((num) => {
      const find = (snap) => snap?.movementScores?.find((m) => m.movementNumber === num) ?? null;
      const confirmed = find(confirmedSnap);
      const signed = find(signedSnap);
      const current = find(currentSnap);
      const changedAfterConfirm =
        confirmed && current && (confirmed.score !== current.score || confirmed.flagged !== current.flagged);
      return { num, confirmed, signed, current, changedAfterConfirm };
    });
}

function MarkCell({ mark }) {
  const { t } = useTranslation("scoreadmin");
  if (!mark) return <TableCell>—</TableCell>;
  return (
    <TableCell>
      {mark.score ?? "—"}
      {mark.flagged ? (
        <Chip size="small" label={t("sheetAudit.flaggedLabel")} sx={{ ml: 0.5 }} />
      ) : null}
    </TableCell>
  );
}

function ChangesetRow({ changeset }) {
  const exceptional = changeset.classification === "EXCEPTIONAL";
  return (
    <TableRow>
      <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(changeset.at)}</TableCell>
      <TableCell>{changeset.actorSub || "—"}</TableCell>
      <TableCell>
        <Chip
          size="small"
          color={exceptional ? "warning" : "default"}
          variant={exceptional ? "filled" : "outlined"}
          label={changeset.classification}
        />
      </TableCell>
      <TableCell>{changeset.reason || "—"}</TableCell>
      <TableCell>
        {(changeset.fields ?? []).map((f, i) => (
          <Box key={i} sx={{ mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {f.field}
            </Typography>
            <Typography variant="body2" component="div">
              {JSON.stringify(f.from)} → {JSON.stringify(f.to)}
            </Typography>
          </Box>
        ))}
      </TableCell>
    </TableRow>
  );
}

function SheetSignature({ sheetId, visible }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!visible || !sheetId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = unwrap(await scoreApi.scoreSheets.getScoreSheetSignature(sheetId));
        if (!cancelled) setUrl(res?.url ?? null);
      } catch {
        if (!cancelled) setUrl(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, sheetId, scoreApi]);

  if (!visible) return null;
  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.signatureTitle")}
      </Typography>
      {url ? (
        <Box
          component="img"
          src={url}
          alt={t("sheetAudit.signatureAlt")}
          sx={{ maxWidth: 320, border: "1px solid", borderColor: "divider", bgcolor: "background.paper" }}
        />
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t("sheetAudit.signatureNotAvailable")}
        </Typography>
      )}
    </Box>
  );
}

function SheetAuditContent({ trail, test }) {
  const { t } = useTranslation("scoreadmin");
  const state = deriveSheetState({
    marksConfirmedAt: trail.marksConfirmedAt,
    completedAt: trail.completedAt,
  });
  const confirmedSnap = trail.baselines?.confirmed?.snapshot ?? null;
  const signedSnap = trail.baselines?.signed?.snapshot ?? null;
  const currentSnap = trail.current ?? null;
  const rows = movementReconciliation(confirmedSnap, signedSnap, currentSnap);
  const hasSigned = Boolean(trail.baselines?.signed);

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <SheetStateChip state={state} />
        <Typography variant="body2" color="text.secondary">
          {t("sheetAudit.judgeLabel", { judgeId: trail.judgeId || "—" })}
        </Typography>
      </Stack>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t("sheetAudit.confirmedLabel")} {fmt(trail.marksConfirmedAt)}
        {trail.marksConfirmedBy
          ? ` ${t("sheetAudit.byActor", { actor: trail.marksConfirmedBy })}`
          : ""}
        {" · "}
        {t("sheetAudit.signedLabel")} {fmt(trail.completedAt)}
        {trail.completedBy ? ` ${t("sheetAudit.byActor", { actor: trail.completedBy })}` : ""}
      </Typography>

      {trail.hasExceptionalChangeAfterConfirm && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {t("sheetAudit.exceptionalChangeWarning")}
        </Alert>
      )}

      {trail.verification?.valid === false && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t("sheetAudit.verificationFailed", { reason: trail.verification.reason })}
        </Alert>
      )}

      {confirmedSnap && rows.length > 0 && (
        <>
          <Typography variant="subtitle2" color="text.primary" gutterBottom>
            {t("sheetAudit.marksTableTitle")}
          </Typography>
          <TableContainer sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t("sheetAudit.columns.mvmt")}</TableCell>
                  <TableCell>{t("sheetAudit.columns.asConfirmed")}</TableCell>
                  {hasSigned && <TableCell>{t("sheetAudit.columns.asSigned")}</TableCell>}
                  <TableCell>{t("sheetAudit.columns.current")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow
                    key={r.num}
                    sx={r.changedAfterConfirm ? { bgcolor: "warning.main", "& *": { color: "warning.contrastText" } } : undefined}
                  >
                    <TableCell>{r.num}</TableCell>
                    <MarkCell mark={r.confirmed} />
                    {hasSigned && <MarkCell mark={r.signed} />}
                    <MarkCell mark={r.current} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {/* #1081 — as-confirmed/as-signed/current all go through the same
              test-aware formatPoints as RideEditor's raw-score readout, so
              the possible-max side matches the earned side's decimal count
              (one for a standard test, two for a freestyle) instead of the
              raw unformatted number this used to print. */}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("sheetAudit.totalLabel")} {t("sheetAudit.asConfirmedLabel")}{" "}
            {formatPoints(confirmedSnap.totalPoints, test)} / {formatPoints(confirmedSnap.maxPoints, test)}
            {signedSnap
              ? ` · ${t("sheetAudit.asSignedLabel")} ${formatPoints(signedSnap.totalPoints, test)} / ${formatPoints(signedSnap.maxPoints, test)}`
              : ""}
            {currentSnap
              ? ` · ${t("sheetAudit.currentLabel")} ${formatPoints(currentSnap.totalPoints, test)} / ${formatPoints(currentSnap.maxPoints, test)}`
              : ""}
          </Typography>
        </>
      )}

      <SheetSignature sheetId={trail.sheetId} visible={hasSigned} />

      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.changesetsTitle", { count: trail.changesets?.length ?? 0 })}
      </Typography>
      {trail.changesets?.length ? (
        <TableContainer sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("sheetAudit.columns.when")}</TableCell>
                <TableCell>{t("sheetAudit.columns.who")}</TableCell>
                <TableCell>{t("sheetAudit.columns.class")}</TableCell>
                <TableCell>{t("sheetAudit.columns.why")}</TableCell>
                <TableCell>{t("sheetAudit.columns.whatChanged")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trail.changesets.map((c) => (
                <ChangesetRow key={c.hash} changeset={c} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t("sheetAudit.noAmendments")}
        </Typography>
      )}

      <Typography variant="subtitle2" color="text.primary" gutterBottom>
        {t("sheetAudit.timelineTitle")}
      </Typography>
      <Stack spacing={0.5}>
        {(trail.events ?? []).map((ev, i) => (
          <Typography key={i} variant="body2" color="text.secondary">
            {fmt(ev.at)} — {ev.type}
            {ev.actorSub ? ` (${ev.actorSub})` : ""}
          </Typography>
        ))}
      </Stack>
    </Box>
  );
}

// `open` mounts the fetch; `rideId` + `sheetId` scope the view to the judge
// tab the admin was already looking at in RideEditor (a ride can carry one
// ScoreSheet per judge — this dialog shows the one in focus, not all of them,
// so it stays legible).
export default function SheetAuditDialog({ open, onClose, rideId, sheetId, test }) {
  const { t } = useTranslation("scoreadmin");
  const scoreApi = useScoreApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [trail, setTrail] = useState(null);

  useEffect(() => {
    if (!open || !rideId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = unwrap(await scoreApi.scoreSheets.getRideAuditTrail(rideId));
        if (!cancelled) setTrail(data);
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, rideId, scoreApi]);

  const sheetTrail = trail?.sheets?.find((s) => s.sheetId === sheetId) ?? null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {t("sheetAudit.title")}
        <IconButton
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "text.secondary" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="error">{error.message}</Alert>}
        {!loading && !error && !sheetTrail && (
          <Typography color="text.secondary">{t("sheetAudit.noTrail")}</Typography>
        )}
        {!loading && !error && sheetTrail && (
          <>
            <SheetAuditContent trail={sheetTrail} test={test} />
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="text.secondary">
                {t("sheetAudit.rideStatusLabel")}
              </Typography>
              <RideStatusBadge status={trail.ride?.status} />
              <Typography variant="caption" color="text.secondary" data-testid="sheet-audit-placing-line">
                {trail.ride?.statusChangedAt
                  ? t("sheetAudit.changedAt", { date: fmt(trail.ride.statusChangedAt) })
                  : ""}
                {" · "}
                {t("sheetAudit.placingLabel", { placing: formatPlacing(t, trail.ride?.placing) })}
                {trail.ride?.adminConfirmedAt
                  ? ` · ${t("sheetAudit.adminFinalized", { date: fmt(trail.ride.adminConfirmedAt) })}`
                  : ""}
              </Typography>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
